import { Alert, Button, Flex } from '@conteg/ui';
import { useTranslation } from 'react-i18next';
import { usePointAuditMessagesQuery } from 'types/generated/graphql';

import AuditViewFilterHeader from '../components/audit-view-filter-header';
import { AuditViewLayout } from '../components/audit-view-layout';
import { DateRangeFilterComponent } from '../components/date-range-filter';
import { EventTypeSelect } from '../components/event-type-select';
import { EventsTable } from '../components/events-table';
import { PointAutocomplete } from '../components/point-autocomplete';
import { StorageUnitSelect } from '../components/storage-unit-select';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { usePointFilters } from '../use-audit-filters';
import { getDefaultDateRange } from '../utils';

/**
 * Point/StorageUnit View
 * Primary filter: Point (autocomplete by ID, name, IP, GUID)
 * Optional filters: StorageUnit, EventType, Date range
 */
export function PointStorageUnitView() {
  const { t } = useTranslation();
  const [filters, setFilters] = usePointFilters();

  // Fetch audit messages when filters are set
  const {
    data: { pointAuditMessages } = {},
    isLoading,
    isError,
    error,
  } = usePointAuditMessagesQuery(
    {
      pointId: filters.pointId,
      from: filters.dateFrom?.toISOString(),
      to: filters.dateTo?.toISOString(),
      pageIndex: parseInt(filters.page) - 1,
      pageSize: DEFAULT_PAGE_SIZE,
      subjects: filters.eventTypes.length > 0 ? filters.eventTypes : undefined,
      storageUnitId: filters.storageUnitId,
      sortOrder: filters.sortOrder,
    },
    { enabled: !!filters.pointId }
  );

  const events = pointAuditMessages?.items ?? [];
  const pagination = {
    currentPage: (pointAuditMessages?.pageIndex ?? 0) + 1,
    totalPages: pointAuditMessages?.totalPages ?? 0,
    totalItems: pointAuditMessages?.totalCount ?? 0,
    itemsPerPage: pointAuditMessages?.pageSize ?? DEFAULT_PAGE_SIZE,
  };

  const hasActiveFilters =
    !!filters.storageUnitId ||
    filters.eventTypes.length > 0 ||
    filters.dateFrom?.getTime() !== getDefaultDateRange().from.getTime() ||
    filters.dateTo?.getTime() !== getDefaultDateRange().to.getTime();

  return (
    <AuditViewLayout currentView="point">
      <AuditViewFilterHeader
        title={t('Events.Tabs.Point')}
        mainFilter={
          <PointAutocomplete
            selectedPointId={filters.pointId}
            onChange={(point) =>
              setFilters({
                pointId: point?.id ?? null,
                storageUnitId: null,
                page: null,
              })
            }
          />
        }
        filters={
          <>
            <DateRangeFilterComponent
              value={{ from: filters.dateFrom, to: filters.dateTo }}
              onChange={(dateRange) =>
                setFilters({
                  dateFrom: dateRange.from,
                  dateTo: dateRange.to,
                })
              }
            />
            <StorageUnitSelect
              pointId={filters.pointId}
              selectedStorageUnitId={filters.storageUnitId}
              onSelect={(storageUnitId) =>
                setFilters({ storageUnitId, page: null })
              }
            />
            <EventTypeSelect
              selectedEventTypes={filters.eventTypes}
              onEventTypesChange={(eventTypes) =>
                setFilters({ eventTypes, page: null })
              }
              view="point"
            />
          </>
        }
        clearFiltersButton={
          hasActiveFilters ? (
            <Button
              title={t('Events.Button.ClearFilters')}
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  storageUnitId: null,
                  eventTypes: null,
                  dateFrom: null,
                  dateTo: null,
                  page: null,
                }));
              }}
              variant="danger"
            />
          ) : undefined
        }
      />

      <Flex flexDirection="column" gap="2rem">
        {isError && (
          <Alert
            type="error"
            title={t('Error.GeneralErrorMessage')}
            message={t('Events.Error.LoadingData.Point')}
            error={error}
          />
        )}

        <EventsTable
          events={events}
          loading={isLoading}
          pagination={pagination}
          onPageChange={(page) => setFilters({ page: page.toString() })}
          sortOrder={filters.sortOrder}
          onSortChange={(sortOrder) => setFilters({ sortOrder })}
          view="point"
        />
      </Flex>
    </AuditViewLayout>
  );
}
